import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import { apiMessage, capabilityApi } from '../../../../api/capability';
import { useCapabilityOrg } from '../../../../queries/useCapability';
import {
  addCapability,
  applyDraft,
  canConfirmLevels,
  capabilityNameProblem,
  confirmCapabilities,
  confirmLevels,
  fillLevels,
  MAX_NAME,
  removeCapability,
  renameCapability,
  roleProblem,
  setCriteria,
  type LevelCriteria,
  type SetupRole,
  type SetupStep,
  type SetupTeam
} from './capabilitySetup';
import { SOURCE_LABEL, type SharedKind } from './capabilitySource';
import AppliesToDialog from './AppliesToDialog';
import { ActionButton } from './designSystem';
import {
  AddForm,
  BODY,
  BRANDY,
  CapabilityRow,
  DIVIDE,
  ElliottNote,
  EmptyState,
  LINE,
  SECTION_TITLE,
  SetupStepper,
  SURFACE,
  TEXT_2,
  TEXT_3
} from './setupParts';
import {
  appliesLabel,
  applyProblem,
  clashingRoleIds,
  isRoleUsable,
  isSetReady,
  setAsRole,
  setFromRole,
  setNameProblem,
  type CapabilitySet
} from './sharedSets';
import SourceChip from './SourceChip';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

/** What Build Assessment / Build Training open with from a shared set: its capabilities and the roles they apply to. */
export interface SharedBuildPreset {
  kind: SharedKind;
  roleIds: string[];
  capabilityIds: string[];
}

const ABOUT: Record<SharedKind, string> = {
  development:
    'Company-wide capabilities that grow people beyond their current role, such as Communication. Set them once here and apply each to the roles that need it.',
  standards:
    'Company-wide capabilities everyone must meet, such as Data Protection. Set them once here and apply each to the roles that need it.'
};

/** The set's checklist: capabilities, their levels, then who they apply to. */
const setSteps = (role: SetupRole, applied: boolean): SetupStep[] => {
  const added = role.capabilities.length > 0;
  const capabilities = Boolean(role.capabilitiesConfirmed && added);
  const levels = capabilities && Boolean(role.levelsConfirmed);
  const status = (done: boolean, open: boolean): SetupStep['status'] => (done ? 'done' : open ? 'current' : 'locked');
  return [
    {
      key: 'capabilities',
      title: 'Confirm capabilities',
      description: 'List the capabilities, then confirm the list.',
      status: status(capabilities, true),
      detail: `${role.capabilities.length} ${role.capabilities.length === 1 ? 'capability' : 'capabilities'}`
    },
    {
      key: 'levels',
      title: 'Confirm levels',
      description: 'Review what Weak, Developing, Capable and Advanced look like for each: one thing you could watch someone do per level, a step up from the one below. Then confirm.',
      status: status(levels, capabilities),
      detail: levels ? 'Confirmed' : 'To do'
    },
    {
      key: 'roles',
      title: 'Apply to roles',
      description: 'Choose the roles each capability is required for.',
      status: status(levels && applied, levels),
      detail: applied ? 'Applied' : 'No roles yet'
    }
  ];
};

/**
 * One of the org admin's shared sets (Development or Standards), laid out as a
 * team's role is: the top bar (the switcher, the set's checklist, Build
 * Assessment and Build Training), then its capabilities and their levels, with
 * who each applies to on its bar. Edits wait on Save Changes; the confirmation
 * for the stage the set is at sits along the bottom.
 */
const SharedSetView: React.FC<{
  kind: SharedKind;
  sets: CapabilitySet[];
  roles: SetupRole[];
  teams: SetupTeam[];
  switcher: React.ReactNode;
  saveSet: (kind: SharedKind, change: (set: CapabilitySet) => CapabilitySet) => Promise<boolean>;
  saving: boolean;
  /** Build Assessment / Build Training, when they are there (flags.ts). */
  onBuild?: (kind: 'assessment' | 'training', preset: SharedBuildPreset) => void;
  trainingReady: boolean;
}> = ({ kind, sets, roles, teams, switcher, saveSet, saving, onBuild, trainingReady }) => {
  const org = useCapabilityOrg();
  const set = sets.find((s) => s.kind === kind) ?? { kind, capabilities: [] };
  const saved = setAsRole(set);
  const [drafts, setDrafts] = useState<Partial<Record<SharedKind, { base: SetupRole; role: SetupRole }>>>({});
  const draft = drafts[kind];
  const role = draft?.role ?? saved;
  const dirty = Boolean(draft);
  const [elliottOn, setElliottOn] = useState(true);
  const [elliottError, setElliottError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const label = SOURCE_LABEL[kind];
  const teamName = (id: string) => teams.find((t) => t.id === id)?.name ?? 'A team';

  const dropDraft = () =>
    setDrafts((current) => {
      const { [kind]: _dropped, ...rest } = current;
      return rest;
    });
  const editSet = (change: (r: SetupRole) => SetupRole | null): boolean => {
    if (drafting) return false;
    const result = change(role);
    if (!result) return false;
    setDrafts((current) => ({ ...current, [kind]: { base: current[kind]?.base ?? saved, role: result } }));
    return true;
  };
  const editWhenAnswered = (base: SetupRole, change: (r: SetupRole) => SetupRole | null) =>
    setDrafts((current) => {
      const result = change(current[kind]?.role ?? base);
      return result ? { ...current, [kind]: { base: current[kind]?.base ?? base, role: result } } : current;
    });
  /** Saves a change made from the set as last saved (as a role), e.g. a confirmation. */
  const save = (change: (r: SetupRole) => SetupRole) =>
    saveSet(kind, (latest) => setFromRole(kind, change(setAsRole(latest))));

  // Why the edits can't be saved: the role rules, a name the other set has, or roles a name clashes in.
  const problemOf = (capabilityId: string): string | null => {
    const capability = role.capabilities.find((c) => c.id === capabilityId);
    if (!capability) return null;
    return (
      capabilityNameProblem(role, capabilityId) ??
      setNameProblem(sets, kind, capability.name) ??
      applyProblem(capability, roles, teamName)
    );
  };
  const problem = dirty
    ? roleProblem(role, saved) ?? role.capabilities.map((c) => problemOf(c.id)).find(Boolean) ?? null
    : null;

  const saveChanges = async () => {
    if (!draft || problem) return;
    const sent = draft;
    const ok = await save((latest) => applyDraft(latest, sent.base, sent.role));
    if (ok) setDrafts((current) => (current[kind] === sent ? (({ [kind]: _s, ...rest }) => rest)(current) : current));
  };

  // Elliott, for the whole organization rather than one role (the set's own endpoints).
  const draftLevelsFor = async (names: string[]) => {
    if (!elliottOn || names.length === 0) return new Map<string, LevelCriteria>();
    try {
      const drafted = await capabilityApi.draftSharedLevels(kind, { capabilities: names }, org);
      setElliottError(null);
      return new Map(drafted.capabilities.map((c) => [c.name.toLowerCase(), c.criteria]));
    } catch (err: any) {
      if (err?.response?.status === 503) setElliottOn(false);
      setElliottError(`${apiMessage(err, 'Elliott could not draft the levels.')} Write them below.`);
      return new Map<string, LevelCriteria>();
    }
  };
  const suggestMore = async () => {
    const base = saved;
    setSuggesting(true);
    try {
      // Every name in use in the organization: a shared capability can't repeat any of them.
      const existing = Array.from(
        new Set([
          ...role.capabilities.map((c) => c.name),
          ...sets.flatMap((s) => s.capabilities.map((c) => c.name)),
          ...roles.flatMap((r) => r.capabilities.map((c) => c.name))
        ])
      );
      const { capabilities } = await capabilityApi.suggestShared(kind, { existing }, org);
      setElliottError(null);
      editWhenAnswered(base, (r) => capabilities.reduce<SetupRole>((acc, name) => addCapability(acc, name) ?? acc, r));
    } catch (err: any) {
      if (err?.response?.status === 503) setElliottOn(false);
      setElliottError(apiMessage(err, 'Elliott could not suggest capabilities.'));
    } finally {
      setSuggesting(false);
    }
  };
  const confirmSetCapabilities = async () => {
    if (!elliottOn) {
      await save((r) => confirmCapabilities(r));
      return;
    }
    setDrafting(true);
    if (!(await save((r) => ({ ...r, capabilitiesConfirmed: true })))) {
      setDrafting(false);
      return;
    }
    const drafted = await draftLevelsFor(role.capabilities.map((c) => c.name));
    if (drafted.size > 0) await save((r) => confirmCapabilities(r, (name) => drafted.get(name.toLowerCase()) ?? {}));
    setDrafting(false);
  };
  const addSetCapability = (name: string): boolean => {
    if (!editSet((r) => addCapability(r, name))) return false;
    if (!role.capabilitiesConfirmed || !elliottOn) return true;
    const base = saved;
    setDrafting(true);
    draftLevelsFor([name.trim()])
      .then((drafted) => {
        const levels = drafted.get(name.trim().toLowerCase());
        if (levels) editWhenAnswered(base, (r) => fillLevels(r, name, levels));
      })
      .finally(() => setDrafting(false));
    return true;
  };

  const savingReason = 'Saving your last change.';
  const saveButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: BRANDY,
          mr: '4px',
          textTransform: 'uppercase'
        }}
      >
        {saving ? 'Saving' : 'Unsaved Changes'}
      </Typography>
      <ActionButton disabled={saving} reason={savingReason} onClick={dropDraft}>
        Discard
      </ActionButton>
      <ActionButton
        primary
        disabled={saving || Boolean(problem)}
        reason={saving ? savingReason : problem ?? undefined}
        onClick={saveChanges}
      >
        Save Changes
      </ActionButton>
    </Box>
  );

  const appliedAnywhere = role.capabilities.some((c) => c.allRoles || (c.roleIds ?? []).length > 0);
  let note: React.ReactNode;
  if (drafting) {
    note = (
      <ElliottNote
        title="Elliott is drafting the levels"
        body={`Writing what Weak, Developing, Capable and Advanced look like for each ${label} capability.`}
      />
    );
  } else if (!role.capabilitiesConfirmed) {
    note = (
      <ElliottNote title={`Choose the ${label} capabilities that matter across your organization, then confirm`}>
        {dirty ? (
          saveButtons
        ) : (
          <ActionButton
            primary
            disabled={saving || role.capabilities.length === 0}
            reason={saving ? savingReason : 'Add at least one capability first.'}
            onClick={confirmSetCapabilities}
          >
            Confirm Capabilities
          </ActionButton>
        )}
      </ElliottNote>
    );
  } else if (!role.levelsConfirmed) {
    note = (
      <ElliottNote
        title={
          elliottOn ? 'Review the levels, then confirm' : 'Write the four levels for each capability, then confirm'
        }
        body="Every level should describe something you could watch someone do. Elliott scores every Roleplay against these levels, whatever the role."
      >
        {dirty ? (
          saveButtons
        ) : (
          <ActionButton
            primary
            disabled={saving || !canConfirmLevels(role)}
            reason={saving ? savingReason : 'Every capability needs all four levels.'}
            onClick={() => save(confirmLevels)}
          >
            Confirm Levels
          </ActionButton>
        )}
      </ElliottNote>
    );
  } else {
    note = (
      <ElliottNote
        done={!dirty && appliedAnywhere}
        title={appliedAnywhere ? `${label} is set up` : `Choose who each ${label} capability is for`}
        body={
          appliedAnywhere
            ? 'Each capability is now required for the roles it applies to. Their teams see it, set by you, and cannot change it. A role is measured on it once its manager has confirmed the role.'
            : 'Open Applies To on each capability and choose its roles. Until then it is part of no role.'
        }
      >
        {dirty && saveButtons}
      </ElliottNote>
    );
  }

  // Build Assessment / Build Training: the set's capabilities and the roles they apply to that can be built on.
  const ready = isSetReady(set) || Boolean(set.established);
  const appliedRoles = roles.filter(
    (r) => isRoleUsable(r, sets) && set.capabilities.some((c) => c.allRoles || c.roleIds.includes(r.id))
  );
  const build = (what: 'assessment' | 'training') =>
    onBuild?.(what, {
      kind,
      roleIds: appliedRoles.map((r) => r.id),
      capabilityIds: set.capabilities.filter((c) => c.allRoles || c.roleIds.length > 0).map((c) => c.id)
    });
  const notBuildable = ready
    ? 'Apply a capability to at least one set-up role first.'
    : 'Confirm the capabilities and their levels first.';
  const canBuild = ready && appliedRoles.length > 0;
  const capability = applying ? role.capabilities.find((c) => c.id === applying) ?? null : null;
  const CARD = { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' };
  const removable = (id: string) => !set.established || !set.capabilities.some((c) => c.id === id);

  return (
    <>
      <Box
        component="section"
        sx={{
          ...CARD,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px 16px',
          flexShrink: 0,
          minHeight: 60,
          px: '16px',
          py: '10px',
          ...fadeInSx,
          // A phone: as on a team's page, one row each for the set, the stepper and the actions.
          [MOBILE_SX]: {
            flexDirection: 'column',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            gap: '12px',
            p: '12px',
            minWidth: 0
          }
        }}
      >
        <Box
          sx={{
            flex: '1 1 320px',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            // A phone: what the set is for goes under the switcher, the full width.
            [MOBILE_SX]: {
              flex: 'none',
              flexWrap: 'wrap',
              rowGap: '10px',
              '& > span[aria-hidden]': { display: 'none' }
            }
          }}
        >
          {switcher}
          {DIVIDE}
          <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_2, minWidth: 0, [MOBILE_SX]: { flexBasis: '100%' } }}>
            {ABOUT[kind]}
          </Typography>
        </Box>
        <SetupStepper
          steps={setSteps(
            saved,
            saved.capabilities.some((c) => c.allRoles || (c.roleIds ?? []).length > 0)
          )}
          teamName={label}
        />
        {onBuild && (
          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: '8px',
              [MOBILE_SX]: { '& > span': { flex: '1 1 40%' }, '& > span > button': { width: '100%', minHeight: 40 } }
            }}
          >
            <ActionButton
              primary={canBuild}
              disabled={!canBuild}
              reason={notBuildable}
              onClick={() => build('assessment')}
            >
              Build Assessment
            </ActionButton>
            {trainingReady && (
              <ActionButton disabled={!canBuild} reason={notBuildable} onClick={() => build('training')}>
                Build Training
              </ActionButton>
            )}
          </Box>
        )}
      </Box>

      <Box
        component="section"
        sx={{
          ...CARD,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...fadeInSx,
          [MOBILE_SX]: { flex: 'none', minHeight: 'auto' }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px 0',
            minHeight: 54,
            px: '16px',
            py: '8px',
            borderBottom: `1px solid ${LINE}`,
            [MOBILE_SX]: { gap: '10px 12px', p: '12px', '& > span[aria-hidden]': { display: 'none' } }
          }}
        >
          <Typography sx={SECTION_TITLE}>Capabilities and levels</Typography>
          {DIVIDE}
          <Typography sx={{ ...SECTION_TITLE, fontWeight: 400, color: TEXT_2, textTransform: 'uppercase', mr: '8px' }}>
            {label}
          </Typography>
          <SourceChip source={kind} />
          <Box sx={{ flex: 1 }} />
          {!drafting && (
            <AddForm
              key={kind}
              compact
              label="Capability name"
              placeholder={role.capabilities.length === 0 ? 'e.g. Communication' : 'Add another capability'}
              button="Add Capability"
              maxLength={MAX_NAME}
              onAdd={addSetCapability}
              refused={(name) => `${label} already has a capability called ${name}.`}
            >
              {elliottOn && !role.capabilitiesConfirmed && (
                <ActionButton disabled={suggesting} reason="Elliott is thinking of capabilities." onClick={suggestMore}>
                  {suggesting ? 'Suggesting...' : 'Suggest With Elliott'}
                </ActionButton>
              )}
            </AddForm>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            p: '16px',
            [MOBILE_SX]: { flex: 'none', overflowY: 'visible', p: '12px' }
          }}
        >
          {role.capabilities.length === 0 && (
            <EmptyState
              title={`No ${label} capabilities yet`}
              body={`Add the first one above, or let Elliott suggest some. ${ABOUT[kind]}`}
            />
          )}
          {role.capabilities.map((c, i) => (
            <CapabilityRow
              key={c.id}
              number={i + 1}
              capability={c}
              source={kind}
              levelsOpen={Boolean(role.capabilitiesConfirmed)}
              drafting={drafting}
              problem={problemOf(c.id)}
              extra={
                <DosenTooltip title="Choose the roles this capability is required for." placement="bottom">
                  <Box
                    component="button"
                    type="button"
                    // Its own name: the tooltip would otherwise stand in for it.
                    aria-label={`Applies To: ${appliesLabel(c, roles)}`}
                    onClick={() => setApplying(c.id)}
                    sx={{
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      height: 26,
                      px: '8px',
                      border: `1px solid ${c.allRoles || (c.roleIds ?? []).length ? '#3A3A3A' : BRANDY}`,
                      borderRadius: '4px',
                      bgcolor: 'transparent',
                      color: c.allRoles || (c.roleIds ?? []).length ? TEXT_2 : BRANDY,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 11.5,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      '&:hover': { borderColor: BRANDY, color: BRANDY }
                    }}
                  >
                    <Box component="span" sx={{ color: TEXT_3 }}>
                      Applies To
                    </Box>
                    {appliesLabel(c, roles)}
                  </Box>
                </DosenTooltip>
              }
              onRename={(name) => editSet((r) => renameCapability(r, c.id, name))}
              onRemove={removable(c.id) ? () => editSet((r) => removeCapability(r, c.id)) : undefined}
              onCriteria={(level, text) => editSet((r) => setCriteria(r, c.id, level, text))}
            />
          ))}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            p: '12px 16px',
            borderTop: `1px solid ${LINE}`,
            [MOBILE_SX]: { p: '12px' }
          }}
        >
          {elliottError && (
            <Typography role="alert" sx={{ ...BODY, fontSize: 12.5, color: '#FF6A55' }}>
              {elliottError}
            </Typography>
          )}
          {note}
        </Box>
      </Box>

      {capability && (
        <AppliesToDialog
          open
          capabilityName={capability.name}
          roles={roles}
          teams={teams}
          value={{ roleIds: capability.roleIds ?? [], allRoles: Boolean(capability.allRoles) }}
          clashing={clashingRoleIds(roles, capability.name)}
          onClose={() => setApplying(null)}
          onSave={(value) => {
            editSet((r) => ({
              ...r,
              capabilities: r.capabilities.map((c) => (c.id === capability.id ? { ...c, ...value } : c))
            }));
            setApplying(null);
          }}
        />
      )}
    </>
  );
};

export default SharedSetView;
